/** Storage page — folder tree sidebar + file browser with upload, search, and view toggle */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Upload, Search, LayoutGrid, List, ArrowLeft } from 'lucide-react'
import { StorageDropZone } from '../components/storage/storage-drop-zone'
import { cn } from '../lib/utils'
import { useAppStore } from '../stores/app-store'
import { useStorageFolderTree, useStorageFiles, useUploadToFolder } from '../hooks/use-storage'
import { useDeleteUpload } from '../hooks/use-uploads'
import { StorageSidebar } from '../components/storage/storage-sidebar'
import { StorageBreadcrumb } from '../components/storage/storage-breadcrumb'
import { StorageFileGrid } from '../components/storage/storage-file-grid'
import { UploadProgressList } from '../components/storage/upload-progress-list'
import { ConfirmDialog } from '../components/ui/confirm-dialog'
import { StoragePreviewModal } from '../components/storage/storage-preview-modal'
import type { StorageFolderNode, StorageFile } from '../hooks/use-storage'
import { useAuth } from '../hooks/use-auth'
import { roleHasPermission } from '../lib/role-permissions'

const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

/** Get direct children of activeFolderId from the flat tree */
function getSubfolders(
  tree: StorageFolderNode[],
  activeFolderId: string | null,
): StorageFolderNode[] {
  if (!activeFolderId) return tree // root: top-level folders
  const map = new Map<string, StorageFolderNode>()
  function flatten(nodes: StorageFolderNode[]) {
    for (const n of nodes) {
      map.set(n.id, n)
      flatten(n.children)
    }
  }
  flatten(tree)
  return map.get(activeFolderId)?.children ?? []
}

export function StoragePage() {
  const { role } = useAuth()
  const canManageStorage = roleHasPermission(role, 'storage:upload')
  const { theme } = useAppStore()
  const isDark = theme === 'dark'
  const navigate = useNavigate()
  const { folderId: routeFolderId } = useParams<{ folderId?: string }>()

  // activeFolderId: null = root (all files), string = specific folder
  const [activeFolderId, setActiveFolderId] = useState<string | null>(routeFolderId ?? null)
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'list' : 'grid',
  )
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [alertMsg, setAlertMsg] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; filename: string } | null>(null)
  const [previewFile, setPreviewFile] = useState<StorageFile | null>(null)

  const { data: treeData } = useStorageFolderTree()
  // When activeFolderId is null, show root-level files; otherwise show folder-scoped files
  const {
    data: filesData,
    isLoading: filesLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useStorageFiles(activeFolderId)
  const uploadToFolder = useUploadToFolder()
  const deleteUpload = useDeleteUpload()

  const folders = treeData?.folders ?? []
  const allFiles = useMemo(() => filesData?.pages.flatMap((p) => p.files) ?? [], [filesData])

  // Sync route → state
  useEffect(() => {
    setActiveFolderId(routeFolderId ?? null)
  }, [routeFolderId])

  const handleSelectFolder = useCallback(
    (id: string | null) => {
      setActiveFolderId(id)
      setSearch('')
      if (id) {
        navigate(`/storage/${id}`, { replace: true })
      } else {
        navigate('/storage', { replace: true })
      }
    },
    [navigate],
  )

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files
      if (!fileList?.length) return
      const oversized: string[] = []
      const validFiles = Array.from(fileList).filter((f) => {
        if (f.size > MAX_FILE_SIZE) {
          oversized.push(f.name)
          return false
        }
        return true
      })
      if (oversized.length)
        setAlertMsg(
          `${oversized.map((n) => `"${n}"`).join(', ')} ${oversized.length === 1 ? 'is' : 'are'} too large (max 100MB)`,
        )
      await Promise.allSettled(validFiles.map((file) => uploadToFolder(file, activeFolderId)))
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [uploadToFolder, activeFolderId],
  )

  const handleFileDelete = useCallback((id: string, filename: string) => {
    setDeleteTarget({ id, filename })
  }, [])

  const confirmFileDelete = useCallback(async () => {
    if (!deleteTarget) return
    await deleteUpload.mutateAsync(deleteTarget.id)
    setDeleteTarget(null)
  }, [deleteTarget, deleteUpload])

  // Filter files client-side by search
  const filteredFiles = search
    ? allFiles.filter((f) => f.filename.toLowerCase().includes(search.toLowerCase()))
    : allFiles

  // Get subfolders for current folder (hide when searching)
  const subfolders = search ? [] : getSubfolders(folders, activeFolderId)

  return (
    <div className={cn('flex h-screen flex-col', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
      {/* Top bar */}
      <div
        className={cn(
          'flex items-center gap-3 border-b px-4 py-3',
          isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
        )}
      >
        <button
          onClick={() => navigate(-1)}
          className={cn(
            'rounded-md p-1',
            isDark
              ? 'text-neutral-500 hover:bg-surface-3 hover:text-neutral-300'
              : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700',
          )}
          title="Go back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <StorageBreadcrumb
          folders={folders}
          activeFolderId={activeFolderId}
          onSelect={handleSelectFolder}
          isDark={isDark}
        />
        <div className="flex-1" />
        {/* Search */}
        <div className="relative hidden sm:block">
          <Search
            className={cn(
              'absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2',
              isDark ? 'text-neutral-500' : 'text-neutral-400',
            )}
          />
          <input
            type="text"
            placeholder="Filter files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn(
              'w-44 rounded-lg border py-1.5 pl-8 pr-3 text-xs outline-none',
              isDark
                ? 'border-white/[0.06] bg-surface-2 text-neutral-200 placeholder-neutral-500 focus:border-brand-500/50'
                : 'border-neutral-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 focus:border-brand-500',
            )}
          />
        </div>
        {/* View toggle */}
        <div
          className={cn(
            'flex rounded-lg border',
            isDark ? 'border-white/[0.06]' : 'border-neutral-200',
          )}
        >
          {(['grid', 'list'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={cn(
                'p-1.5',
                mode === 'grid' ? 'rounded-l-lg' : 'rounded-r-lg',
                viewMode === mode
                  ? isDark
                    ? 'bg-surface-3 text-neutral-200'
                    : 'bg-neutral-100 text-neutral-700'
                  : isDark
                    ? 'text-neutral-500 hover:text-neutral-300'
                    : 'text-neutral-400 hover:text-neutral-600',
              )}
              title={mode === 'grid' ? 'Grid view' : 'List view'}
            >
              {mode === 'grid' ? (
                <LayoutGrid className="h-3.5 w-3.5" />
              ) : (
                <List className="h-3.5 w-3.5" />
              )}
            </button>
          ))}
        </div>
        {/* Upload */}
        {canManageStorage && (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-500"
          >
            <Upload className="h-3.5 w-3.5" />
            Upload
          </button>
        )}
        {canManageStorage && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleUpload}
            className="hidden"
          />
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — hidden on mobile */}
        <div className="hidden md:flex">
          <StorageSidebar
            activeFolderId={activeFolderId}
            onSelect={handleSelectFolder}
            isDark={isDark}
            canManage={canManageStorage}
          />
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Upload progress */}
          <UploadProgressList isDark={isDark} />

          {/* File grid */}
          <div className="flex-1 overflow-y-auto p-4">
            <StorageFileGrid
              subfolders={subfolders}
              files={filteredFiles}
              viewMode={viewMode}
              isDark={isDark}
              onFolderOpen={handleSelectFolder}
              onFileDelete={handleFileDelete}
              onFilePreview={setPreviewFile}
              isLoading={filesLoading}
              canManage={canManageStorage}
            />
            {/* Load more trigger */}
            {hasNextPage && !search && (
              <div className="flex justify-center py-4">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className={cn(
                    'rounded-lg px-4 py-2 text-xs font-medium',
                    isDark
                      ? 'bg-surface-3 text-neutral-300 hover:bg-surface-4'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200',
                    isFetchingNextPage && 'opacity-50',
                  )}
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alert dialog — file too large */}
      <ConfirmDialog
        open={!!alertMsg}
        onClose={() => setAlertMsg(null)}
        title="Upload error"
        description={alertMsg ?? ''}
        variant="info"
      />

      {/* Confirm dialog — delete file */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmFileDelete}
        title="Delete file"
        description={`Are you sure you want to delete "${deleteTarget?.filename}"?`}
        confirmLabel="Delete"
        variant="danger"
      />

      {/* Media preview lightbox */}
      <StoragePreviewModal
        file={previewFile}
        files={filteredFiles}
        onClose={() => setPreviewFile(null)}
        onNavigate={setPreviewFile}
      />

      {/* Drag & drop overlay — only active on storage page */}
      {canManageStorage && <StorageDropZone activeFolderId={activeFolderId} isDark={isDark} />}
    </div>
  )
}
